import { getById, getBySlug, getByTitle } from '@apps/utils/contentful'

export const SIM_SELECTION_CONTENT_PAGE_IDS = {
  consumer: 'haveSimPage',
  dealer: 'dealerSimSelectionPage',
  dealerLegacy: 'dealerHaveSimPage',
  legacyShared: 'haveSimPage'
}

const getDealerSimSelectionPageIds = () => [
  SIM_SELECTION_CONTENT_PAGE_IDS.dealer,
  SIM_SELECTION_CONTENT_PAGE_IDS.dealerLegacy,
  SIM_SELECTION_CONTENT_PAGE_IDS.legacyShared
]

const getDealerSimSelectionPageLabel = () => getDealerSimSelectionPageIds().join(' or ')

const REQUIRED_DEALER_SIM_SELECTION_CONTENT = {
  shortTextFields: [
    'simSelectionRadioHeading',
    'previousStepLabel',
    'newESimOption',
    'havePhysicalSimOption',
    'eSimCost',
    'pSimCost',
    'beforeYouProceedHeading',
    'eSimImeiInstructions',
    'checkPhoneSettings',
    'dialPhoneCode',
    'dealerEIDInputLabel',
    'dealerEIDInputPlaceholder',
    'eidRequiredError',
    'eidInvalidError',
    'dealerOptionalImeiInputLabel',
    'checkIMEI',
    'imeiNotFound',
    'simCardNumberHeading',
    'simCardNumberInputLabel',
    'physicalSimStoreLocatorMessage',
    'storeLocator',
    'storeLink',
    'simRequiredError',
    'simLengthError',
    'simUnavailableError',
    'simCardNumberValidated',
    'sim-card-number-location',
    'sim-card-number-details',
    'eSIM',
    'lteNetwork',
    'manufacturer',
    'model',
    'imei',
    'simPurchaseUnavailableTitle',
    'simPurchaseUnavailableMessage'
  ],
  longTextFields: [],
  images: [
    'sim-card-front-back'
  ]
}

const getDealerSimSelectionPage = (pageContent) => {
  if (!Array.isArray(pageContent)) return {}

  return getDealerSimSelectionPageIds()
    .map((pageId) => getById(pageContent, pageId))
    .find(Boolean) || {}
}

const hasLoadedPageContent = (pageContent) => Array.isArray(pageContent) && pageContent.length > 0

export const getMissingSimSelectionContent = (pageContent, language, userType) => {
  if (userType !== 'dealer') return []

  if (!hasLoadedPageContent(pageContent) || !language) return []

  const page = getDealerSimSelectionPage(pageContent)

  if (!page?.id) {
    return [
      {
        type: 'page',
        key: getDealerSimSelectionPageLabel()
      }
    ]
  }

  const localizedPage = page?.[language]

  if (!localizedPage) {
    return [
      {
        type: 'locale',
        key: `${page.id}.${language}`
      }
    ]
  }

  const missingShortTextFields = REQUIRED_DEALER_SIM_SELECTION_CONTENT.shortTextFields
    .filter((slug) => !getBySlug(localizedPage.shortTextFields, slug)?.value)
    .map((slug) => ({ type: 'shortTextFields', key: slug, pageId: page.id }))

  const missingLongTextFields = REQUIRED_DEALER_SIM_SELECTION_CONTENT.longTextFields
    .filter((slug) => !getBySlug(localizedPage.longTextFields, slug)?.value)
    .map((slug) => ({ type: 'longTextFields', key: slug, pageId: page.id }))

  const missingImages = REQUIRED_DEALER_SIM_SELECTION_CONTENT.images
    .filter((title) => !getByTitle(localizedPage.images, title)?.url)
    .map((title) => ({ type: 'images', key: title, pageId: page.id }))

  return [
    ...missingShortTextFields,
    ...missingLongTextFields,
    ...missingImages
  ]
}

const getContentfulScope = (userType) => {
  return userType === 'dealer'
    ? 'dealer SIM selection Contentful page'
    : SIM_SELECTION_CONTENT_PAGE_IDS.consumer
}

export const getSimSelectionPageContent = (pageContent, language, userType) => {
  if (!hasLoadedPageContent(pageContent) || !language) return {}

  const page = userType === 'dealer'
    ? getDealerSimSelectionPage(pageContent)
    : getById(pageContent, SIM_SELECTION_CONTENT_PAGE_IDS.consumer)

  if (userType === 'dealer' && !page?.id) {
    throw new Error(
      `Missing dealer SIM selection Contentful page: ${getDealerSimSelectionPageLabel()}`
    )
  }

  const localizedPage = page?.[language]

  if (userType === 'dealer' && !localizedPage) {
    throw new Error(`Missing Contentful locale "${language}" for page: ${page.id}`)
  }

  return localizedPage || {}
}

export const getSimSelectionSlugValue = (
  fields,
  slug,
  userType,
  fieldType = 'shortTextFields'
) => {
  const value = getBySlug(fields, slug)?.value

  if (userType === 'dealer' && (value === undefined || value === null || value === '')) {
    throw new Error(
      `Missing Contentful ${fieldType} slug "${slug}" on ${getContentfulScope(userType)}`
    )
  }

  return value
}

export const getPreferredSimSelectionText = (longTextFields, shortTextFields, slug) => {
  const longTextValue = getBySlug(longTextFields, slug)?.value
  const trimmedLongTextValue = typeof longTextValue === 'string' ? longTextValue.trim() : ''

  if (trimmedLongTextValue) {
    return trimmedLongTextValue
  }

  return getBySlug(shortTextFields, slug)?.value
}

export const getSimSelectionImageByTitle = (images, title, userType) => {
  const image = getByTitle(images, title)

  if (userType === 'dealer' && !image?.url) {
    throw new Error(
      `Missing Contentful image title "${title}" on ${getContentfulScope(userType)}`
    )
  }

  return image
}
